import { type ClassValue, clsx } from 'clsx'
import BN from 'bn.js'

/**
 * Utility function to combine class names conditionally
 * Uses clsx under the hood for efficient class merging
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

/**
 * Cyberpunk theme configuration
 * Central place to manage all cyberpunk styling constants
 */
export const CYBERPUNK_THEME = {
  colors: {
    primary: {
      cyan: '#00E0FF',
      magenta: '#FF00CC', 
      purple: '#9945FF',
      green: '#00FF88',
      yellow: '#FFD700',
    },
    background: {
      main: '#050812',
      card: '#0c101c',
      panel: '#0f1419',
      surface: '#151b26',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0BEC5',
      muted: '#6B7280',
    },
    status: {
      success: '#00FF88',
      warning: '#FFD700',
      error: '#FF5555',
      info: '#00E0FF',
    }
  },
  animations: {
    durations: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    easings: {
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    }
  },
  shadows: {
    glow: {
      cyan: '0 0 20px rgba(0, 224, 255, 0.5)',
      magenta: '0 0 20px rgba(255, 0, 204, 0.5)',
      purple: '0 0 20px rgba(153, 69, 255, 0.5)',
      green: '0 0 20px rgba(0, 255, 136, 0.5)',
      yellow: '0 0 20px rgba(255, 215, 0, 0.5)',
    }
  }
} as const

/**
 * Get a cyberpunk color with optional transparency
 * Provides type-safe access to theme colors with alpha support
 */
export function getCyberColor(
  category: keyof typeof CYBERPUNK_THEME.colors,
  color: string,
  alpha?: number
): string {
  const colorValue = (CYBERPUNK_THEME.colors[category] as any)[color]
  if (!colorValue) {
    console.warn(`Color not found: ${category}.${color}`)
    return CYBERPUNK_THEME.colors.primary.cyan
  }
  
  if (alpha !== undefined) {
    // Convert hex to rgba
    const hex = colorValue.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  
  return colorValue
}

/**
 * Generate cyberpunk glow effect classes
 * Creates dynamic glow effects for UI elements
 */
export function generateGlowClasses(color: keyof typeof CYBERPUNK_THEME.colors.primary): string {
  return cn(
    'transition-all duration-300',
    `hover:shadow-[${CYBERPUNK_THEME.shadows.glow[color]}]`,
    `hover:border-[${CYBERPUNK_THEME.colors.primary[color]}]`,
    'hover:scale-[1.02]'
  )
}

/**
 * Number formatting utilities
 * Provides consistent number display across the application
 */
export class NumberUtils {
  /**
   * Format a number with commas as thousands separators
   * Handles both regular numbers and BN instances
   */
  static formatWithCommas(value: number | string | BN): string {
    const num = typeof value === 'string' ? parseFloat(value) : 
                typeof value === 'number' ? value : value.toNumber()
    return new Intl.NumberFormat('en-US').format(num)
  }

  /**
   * Format currency values with appropriate decimal places
   * Automatically adjusts precision based on value magnitude
   */
  static formatCurrency(value: number | BN, symbol: string = '', decimals?: number): string {
    const num = typeof value === 'number' ? value : value.toNumber()
    
    let precision = decimals
    if (precision === undefined) {
      if (num === 0) precision = 0
      else if (num < 0.01) precision = 6
      else if (num < 1) precision = 4
      else if (num < 100) precision = 2
      else precision = 0
    }
    
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision
    }).format(num)
    
    return symbol ? `${symbol}${formatted}` : formatted
  }

  /**
   * Format percentage with proper scaling and precision
   * Handles edge cases like very small percentages
   */
  static formatPercentage(value: number, precision: number = 2): string {
    if (value === 0) return '0%'
    if (value < 0.01 && value > 0) return '<0.01%'
    return `${value.toFixed(precision)}%`
  }

  /**
   * Format large numbers with unit suffixes (K, M, B, T)
   * Makes large values more readable in UI contexts
   */
  static formatCompact(value: number | BN): string {
    const num = typeof value === 'number' ? value : value.toNumber()
    
    const units = [
      { value: 1e12, suffix: 'T' },
      { value: 1e9, suffix: 'B' },
      { value: 1e6, suffix: 'M' },
      { value: 1e3, suffix: 'K' }
    ]
    
    for (const unit of units) {
      if (Math.abs(num) >= unit.value) {
        const scaled = num / unit.value
        const precision = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2
        return `${scaled.toFixed(precision)}${unit.suffix}`
      }
    }
    
    return this.formatWithCommas(num)
  }

  /**
   * Safely parse a string to number with validation
   * Returns null for invalid inputs instead of NaN
   */
  static safeParseFloat(value: string): number | null {
    if (typeof value !== 'string' || value.trim() === '') return null
    
    const parsed = parseFloat(value.replace(/,/g, ''))
    return isNaN(parsed) ? null : parsed
  }

  /**
   * Clamp a number between min and max values
   * Ensures values stay within acceptable ranges
   */
  static clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
  }
}

/**
 * Date and time formatting utilities
 * Provides consistent date/time handling across components
 */
export class DateUtils {
  /**
   * Format Unix timestamp to localized date string
   * Handles both seconds and milliseconds timestamps
   */
  static formatDate(timestamp: number | BN, options?: Intl.DateTimeFormatOptions): string {
    const ts = typeof timestamp === 'number' ? timestamp : timestamp.toNumber()
    
    // Handle both seconds and milliseconds
    const date = new Date(ts > 1e10 ? ts : ts * 1000)
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }
    
    return date.toLocaleDateString('en-US', options || defaultOptions)
  }

  /**
   * Format timestamp to include both date and time
   * Useful for detailed timestamps
   */
  static formatDateTime(timestamp: number | BN): string {
    const ts = typeof timestamp === 'number' ? timestamp : timestamp.toNumber()
    const date = new Date(ts > 1e10 ? ts : ts * 1000)
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Get relative time string (e.g., "2 hours ago", "in 3 days")
   * Provides human-readable relative dates
   */
  static getRelativeTime(timestamp: number | BN): string {
    const ts = typeof timestamp === 'number' ? timestamp : timestamp.toNumber()
    const date = new Date(ts > 1e10 ? ts : ts * 1000)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (Math.abs(diffDays) >= 1) {
      return diffDays > 0 ? `in ${diffDays} day${diffDays > 1 ? 's' : ''}` 
                          : `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} ago`
    }
    
    if (Math.abs(diffHours) >= 1) {
      return diffHours > 0 ? `in ${diffHours} hour${diffHours > 1 ? 's' : ''}` 
                           : `${Math.abs(diffHours)} hour${Math.abs(diffHours) > 1 ? 's' : ''} ago`
    }
    
    if (Math.abs(diffMinutes) >= 1) {
      return diffMinutes > 0 ? `in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}` 
                             : `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) > 1 ? 's' : ''} ago`
    }
    
    return 'just now'
  }

  /**
   * Calculate time remaining until a deadline
   * Returns structured time components for countdown displays
   */
  static getTimeRemaining(deadline: number | BN): {
    total: number
    days: number
    hours: number
    minutes: number
    seconds: number
    expired: boolean
  } {
    const deadlineTs = typeof deadline === 'number' ? deadline : deadline.toNumber()
    const now = Math.floor(Date.now() / 1000)
    const total = deadlineTs - now

    if (total <= 0) {
      return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
    }

    return {
      total,
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
      expired: false
    }
  }

  /**
   * Check if a timestamp is within the last 24 hours
   * Useful for highlighting recent activity
   */
  static isRecent(timestamp: number | BN): boolean {
    const ts = typeof timestamp === 'number' ? timestamp : timestamp.toNumber()
    const date = new Date(ts > 1e10 ? ts : ts * 1000)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    return diffMs < 24 * 60 * 60 * 1000 // 24 hours in milliseconds
  }
}

/**
 * Validation utilities
 * Provides common validation patterns used across the application
 */
export class ValidationUtils {
  /**
   * Validate Solana public key format
   * Checks if string is a valid base58 address
   */
  static isValidPublicKey(address: string): boolean {
    try {
      // Basic length and character checks
      if (typeof address !== 'string' || address.length < 32 || address.length > 44) {
        return false
      }
      
      // Check for valid base58 characters
      const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/
      if (!base58Regex.test(address)) {
        return false
      }
      
      return true
    } catch {
      return false
    }
  }

  /**
   * Validate positive number input
   * Ensures input is a valid positive number
   */
  static isPositiveNumber(value: string | number): boolean {
    const num = typeof value === 'string' ? NumberUtils.safeParseFloat(value) : value
    return num !== null && num > 0 && isFinite(num)
  }

  /**
   * Validate email format (basic)
   * Simple email validation for basic use cases
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Sanitize HTML to prevent XSS
   * Basic HTML sanitization for user-generated content
   */
  static sanitizeHTML(html: string): string {
    const div = document.createElement('div')
    div.textContent = html
    return div.innerHTML
  }

  /**
   * Validate URL format
   * Checks if string is a valid URL
   */
  static isValidURL(url: string): boolean {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }
}

/**
 * Local storage utilities with error handling
 * Provides safe localStorage operations with fallbacks
 */
export class StorageUtils {
  /**
   * Safely get item from localStorage with JSON parsing
   * Returns null if item doesn't exist or parsing fails
   */
  static getItem<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.warn(`Error reading from localStorage key "${key}":`, error)
      return null
    }
  }

  /**
   * Safely set item in localStorage with JSON stringification
   * Handles errors gracefully and provides feedback
   */
  static setItem<T>(key: string, value: T): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(value))
      return true
    } catch (error) {
      console.warn(`Error writing to localStorage key "${key}":`, error)
      return false
    }
  }

  /**
   * Remove item from localStorage safely
   * Always succeeds, even if key doesn't exist
   */
  static removeItem(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error)
    }
  }

  /**
   * Check if localStorage is available
   * Tests both existence and functionality
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__test_localStorage__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }
}

/**
 * Accessibility utilities
 * Provides helpers for better accessibility support
 */
export class A11yUtils {
  /**
   * Generate unique ID for form elements
   * Ensures unique IDs for proper label association
   */
  static generateId(prefix: string = 'element'): string {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Check if reduced motion is preferred
   * Respects user's motion preferences for accessibility
   */
  static prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /**
   * Announce text to screen readers
   * Uses aria-live regions for dynamic content updates
   */
  static announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcer = document.createElement('div')
    announcer.setAttribute('aria-live', priority)
    announcer.setAttribute('aria-atomic', 'true')
    announcer.style.position = 'absolute'
    announcer.style.left = '-10000px'
    announcer.style.width = '1px'
    announcer.style.height = '1px'
    announcer.style.overflow = 'hidden'
    
    document.body.appendChild(announcer)
    announcer.textContent = message
    
    setTimeout(() => document.body.removeChild(announcer), 1000)
  }

  /**
   * Trap focus within an element
   * Useful for modal dialogs and dropdowns
   */
  static trapFocus(element: HTMLElement): () => void {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0] as HTMLElement
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

    function handleTabKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus()
          e.preventDefault()
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus()
          e.preventDefault()
        }
      }
    }

    element.addEventListener('keydown', handleTabKey)

    // Return cleanup function
    return () => element.removeEventListener('keydown', handleTabKey)
  }
}

/**
 * Debounce function for performance optimization
 * Delays function execution until after a period of inactivity
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Throttle function for performance optimization
 * Limits function execution to at most once per specified interval
 */
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  let lastFunc: NodeJS.Timeout
  let lastRan: number

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      lastRan = Date.now()
      inThrottle = true
    } else {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= wait) {
          func(...args)
          lastRan = Date.now()
        }
      }, Math.max(wait - (Date.now() - lastRan), 0))
    }
  }
}

/**
 * Deep copy an object using JSON methods
 * Simple deep copy for serializable objects
 */
export function deepCopy<T>(obj: T): T {
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch {
    console.warn('Failed to deep copy object, returning original')
    return obj
  }
}

/**
 * Sleep function for async operations
 * Useful for adding delays in async workflows
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Generate a random hex color
 * Returns a random color in hex format
 */
export function getRandomHexColor(): string {
  return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
}

/**
 * Check if code is running in development mode
 * Useful for conditional development features
 */
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development'
}

/**
 * Safe console.log that only runs in development
 * Prevents console pollution in production
 */
export function devLog(...args: any[]): void {
  if (isDevelopment()) {
    console.log('[DEV]', ...args)
  }
}